import React from 'react';
import PropTypes from 'prop-types';
import { useReducedMotion, motion } from 'framer-motion';

/**
 * MotionFade - Production-ready reusable animation component
 *
 * A performant fade-in animation component with scroll-triggered viewport detection.
 * Automatically respects user's prefers-reduced-motion preference for accessibility.
 * Designed to be scalable across the entire application.
 *
 * Features:
 * - Viewport-triggered animations (once=true means animation plays only once when element enters viewport)
 * - Reduced motion support for accessibility compliance (WCAG 2.1)
 * - Stagger support for sequential animations of multiple items
 * - Configurable easing, duration, and direction
 * - Supports any HTML element via 'as' prop
 * - MUI sx prop integration for styling
 * - TypeScript-friendly prop documentation
 *
 * @param {React.ReactNode} children - Content to animate
 * @param {number} delay - Delay before animation starts (seconds). Default: 0.1
 * @param {number} duration - Animation duration (seconds). Default: 0.6
 * @param {number} y - Y-axis movement in pixels (when not using reduced motion). Default: 18
 * @param {number} x - X-axis movement in pixels (when not using reduced motion). Default: 0
 * @param {string} direction - Direction of movement: 'up', 'down', 'left', 'right'. Overrides x/y if set.
 * @param {boolean} once - If true, animation plays only once when element enters viewport. Default: true
 * @param {string} as - HTML element to render: 'div', 'section', 'article', 'header', 'footer', 'main', etc. Default: 'div'
 * @param {string} ease - Easing function: 'easeOut', 'easeInOut', 'easeIn', 'circOut', 'backOut'. Default: 'easeOut'
 * @param {Object} viewport - Custom viewport settings { once, margin, amount }. Overrides 'once' prop if provided.
 * @param {Object} style - Inline CSS styles
 * @param {Object} sx - MUI sx prop for styling (takes precedence over style)
 * @param {string} className - CSS class name
 * @param {number} staggerDelay - Additional delay multiplier for stagger effects (when used in lists). Default: 0
 *
 * @example
 * // Basic fade-in
 * <MotionFade><div>Content fades in</div></MotionFade>
 *
 * @example
 * // Slide up with custom duration
 * <MotionFade duration={0.8}><h2>Title slides up</h2></MotionFade>
 *
 * @example
 * // List with stagger effect
 * {items.map((item, index) => (
 *   <MotionFade key={item.id} delay={0.1 + index * 0.1}>
 *     <Card>{item.title}</Card>
 *   </MotionFade>
 * ))}
 *
 * @example
 * // Reduced motion automatically respected
 * <MotionFade>
 *   Content still appears, just without motion on prefers-reduced-motion
 * </MotionFade>
 *
 * @returns {React.ReactElement} Animated component
 */
const MotionFade = ({
  children,
  delay = 0.1,
  duration = 0.6,
  y = 18,
  x = 0,
  direction = null,
  once = true,
  as = 'div',
  ease = 'easeOut',
  viewport = null,
  style,
  sx,
  className,
  staggerDelay = 0,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const Comp = motion[as] || motion.div;

  // Direction mapping for common animation directions
  const directionMap = {
    up: { x: 0, y: 18 },
    down: { x: 0, y: -18 },
    left: { x: 18, y: 0 },
    right: { x: -18, y: 0 },
  };

  const movement = direction ? directionMap[direction] : { x, y };

  // Variants handle both normal and reduced-motion states
  const variants = shouldReduceMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.3 } },
      }
    : {
        hidden: { opacity: 0, ...movement },
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          transition: {
            duration,
            ease,
            delay: delay + staggerDelay,
          },
        },
      };

  // Viewport settings with sensible defaults
  const viewportSettings = viewport || {
    once,
    // Trigger animation when element is 20% from bottom of viewport
    margin: '0px 0px -20% 0px',
    amount: 'some', // Animation triggers when any part of element is visible
  };

  return (
    <Comp
      initial="hidden"
      whileInView="visible"
      viewport={viewportSettings}
      variants={variants}
      style={style}
      sx={sx}
      className={className}
    >
      {children}
    </Comp>
  );
};

MotionFade.propTypes = {
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  duration: PropTypes.number,
  y: PropTypes.number,
  x: PropTypes.number,
  direction: PropTypes.oneOf(['up', 'down', 'left', 'right', null]),
  once: PropTypes.bool,
  as: PropTypes.string,
  ease: PropTypes.oneOf(['easeOut', 'easeInOut', 'easeIn', 'circOut', 'backOut']),
  viewport: PropTypes.object,
  style: PropTypes.object,
  sx: PropTypes.object,
  className: PropTypes.string,
  staggerDelay: PropTypes.number,
};

export default MotionFade;